import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from 'react-toastify'
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import { months_list, years_list } from "../../Shared/List";
import SelectField from "../../Shared/SelectField"

import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const MotorMonthlyReportSummary = () => {
  const [errors, setErrors] = useState({})
  const [data, setData] = useState([])
  const [yearOption, setYearByOption] = useState([])
  const [monthOption, setMonthByOption] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const gridRef = useRef();
  const navigate = useNavigate()

  const user = JSON.parse(localStorage.getItem('user'))
  const billingDate = user?.branch?.billing_date

  const [formData, setFormData] = useState({
      year: billingDate?.year || new Date().getFullYear(),
      month: billingDate?.month || new Date().getMonth() + 1,
      with_fuel: "No"
  })
  const {year, month, with_fuel} = formData

  let isMounted = true
  const getMonthlyReport = async (payload) => {
      setIsLoading(true)
      try {
          const response = await Server.get(`/motor-reports/monthly?year=${payload.year}&month=${payload.month}&with_fuel=${with_fuel}`)
          if(isMounted){
              const years = years_list.map(y => ({ id: y.id, key: y.key, name: y.name }))
              const months = months_list.map(m => ({ id: m.id, key: m.key, name: m.name }))

              setData(response?.data)
              setYearByOption(years)
              setMonthByOption(months)
          }
          setIsLoading(false)
      } catch (error) {
          setIsLoading(false)
          if(error?.response?.status === 401){
              localStorage.removeItem('user')
              navigate('/login')
          }else if (error?.response?.status === 422) {
              setErrors({})
              error.response?.data?.detail.map((err) => (
                  setErrors((prevState) => ({
                      ...prevState,
                      [err.loc[1]]: err.msg
                  }))
              ))
          }else if (error?.response?.status === 403){
              toast.error(error?.response?.data?.detail || 'Forbidden');
              navigate('/');
          }else {
              toast.error(error?.response?.data?.detail || 'Failed to load motor monthly report')
          }
      }
  }

  useEffect(() => {
      getMonthlyReport({ year, month, with_fuel })
      return () => (isMounted = false);
  }, [])

  const onChange = (e) => {
      setFormData((prevState) => ({
          ...prevState,
          [e.target.name]: e.target.value
      }))
  }

  const onDoubleClick = (payload) => {
      navigate(`/motor-daily-reports`, { state: {payload: payload}})
  }

  const onSubmit = (e) => {
      e.preventDefault()
      getMonthlyReport({ year, month, with_fuel })
  }

  let gridApi
    const onGridReady=(params)=>{
        gridApi=params.api
    }
      const onExportClick=()=>{
        gridApi.exportDataAsCsv({ fileName: `Motor Monthly Report ${months_list[month - 1].name} ${year}.csv` })
    }

  const defaultColDef = useMemo(() => ({
      filterParams: { buttons: [] }
  }), []);

  const onFirstDataRendered = (params) => {
      params.api.autoSizeAllColumns();
  }

  const onGridSizeChanged = (params) => {
      params.api.autoSizeAllColumns();
  }

  // Generate hour columns dynamically
  const hourColumns = Array.from({ length: 24 }, (_, i) => {
      const label = `${String(i).padStart(2, '0')}:00`
      return {
          headerName: label,
          field: label,
          sortable: true,
          filter: true,
      }
  })

  const columnDefs = useMemo(() => [
      {
          headerName: "#",
          filter: false,
          maxWidth: 70,
          sortable: true,
          valueFormatter: (params) => isNaN(params.node.id) ? "" : `${Number(params.node.rowIndex) + 1}`,
      },
      { headerName: "Day", field: "day", sortable: true, filter: true },
      { headerName: "Total kW", field: "total_kw", sortable: true, filter: 'agNumberColumnFilter' },
      ...(with_fuel === "Yes" ? [
          { headerName: "Total Fuel", field: "total_fuel", sortable: true, filter: 'agNumberColumnFilter' },
      ] : []),
      { headerName: "Average", field: "average", sortable: true, filter: 'agNumberColumnFilter' },
      ...hourColumns,
  ], [with_fuel])

  if(isLoading){
      return <Spinner/>
  }

return (
  <>
      <form onSubmit={onSubmit}>
          <div className="row">
              <div className="col-md-2">
                  <SelectField
                      name="year"
                      value={parseInt(year)}
                      options={yearOption}
                      onChange={onChange}
                      error={errors.year}
                  />
              </div>
              <div className="col-md-2">
                  <SelectField
                      name="month"
                      value={parseInt(month)}
                      options={monthOption}
                      onChange={onChange}
                      error={errors.month}
                  />
              </div>
              <div className="col-md-2">
                  <SelectField
                      name="with_fuel"
                      value={with_fuel}
                      options={[
                          { id: "No", key: "No", name: "kW Only" },
                          { id: "Yes", key: "Yes", name: "kW + Fuel" },
                      ]}
                      onChange={onChange}
                      error={errors.with_fuel}
                  />
              </div>
              <div className="col-md-4">
                  <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
              </div>
              <div className="col-md-2">
                  <button type="button" className="btn btn-success mt-1 ms-2" onClick={onExportClick} style={{float: 'right'}} title="Export to CSV">Export</button>
              </div>
          </div>
          <br/>
      </form>
      <div className="card mt-0">
          <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
              <h3 className="card-title">{months_list[formData.month - 1].name} {year} Motor Monthly Report</h3>
          </div>
          <div className="card-body">
              <div className="row">
                  <div className="col-md-12">
                      <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                      <AgGridReact
                          ref={gridRef}
                          suppressMenuHide={true}
                          onFirstDataRendered={onFirstDataRendered}
                          onGridSizeChanged={onGridSizeChanged}
                          defaultColDef={defaultColDef}
                          onRowDoubleClicked={(e) => {
                              if (e.node.rowPinned) return;
                              onDoubleClick({ year, month, day: e.data.day })
                          }}
                          rowData={data}
                          domLayout={"normal"}
                          rowSelection={"multiple"}
                          animateRows={true}
                          pagination={false}
                          onGridReady={onGridReady}
                          resizable={true}
                          enableCellTextSelection={true}
                          suppressCellFocus={true}
                          columnDefs={columnDefs}
                      />
                  </div>
                  </div>
              </div>
          </div>
      </div>
    <br/>
  </>
)
}

export default MotorMonthlyReportSummary